/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useToast } from '../ui/use-toast';
import { Loader2, Plus, Edit, Trash2, Tag } from 'lucide-react';
import { registryActions } from '../../actions/registry.action';
import {
  RegistryEntry,
  RegistryTag,
  RegistryFieldData,
  RegistryFormData,
  REGISTRY_CLASSES,
  ORACLE_TAXONOMIES,
} from '../../types/registry/registry';

const emptyField = (): RegistryFieldData => ({
  value: '',
  provenance: 'illustrative',
  source: '',
  asOf: '',
  note: '',
});

const emptyForm = (): RegistryFormData => ({
  name: '',
  issuer: '',
  assetClass: 'Treasuries',
  real: true,
  jurisdiction: '',
  structure: '',
  oracleTaxonomy: 'hybrid',
  custodian: emptyField(),
  minimum: emptyField(),
  publishedYield: emptyField(),
  aum: emptyField(),
  documents: [],
  note: '',
});

const RegistryManagement = () => {
  const [entries, setEntries] = useState<RegistryEntry[]>([]);
  const [tags, setTags] = useState<RegistryTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<RegistryEntry | null>(null);
  const [form, setForm] = useState<RegistryFormData>(emptyForm());
  const [docsText, setDocsText] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showTags, setShowTags] = useState(false);
  const [newTag, setNewTag] = useState('');

  const [search, setSearch] = useState('');
  const [clsFilter, setClsFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const { toast } = useToast();

  useEffect(() => {
    fetchEntries();
    fetchTags();
  }, []);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const res = await registryActions.getEntries();
      setEntries(res.data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to fetch registry', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await registryActions.getTags();
      setTags(res.data || []);
    } catch (e) {
      /* non-fatal */
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setDocsText('');
    setShowForm(true);
  };

  const openEdit = (en: RegistryEntry) => {
    setEditing(en);
    setForm({
      name: en.name,
      issuer: en.issuer,
      assetClass: en.assetClass,
      real: en.real,
      jurisdiction: en.jurisdiction || '',
      structure: en.structure || '',
      oracleTaxonomy: en.oracleTaxonomy || 'hybrid',
      custodian: { ...emptyField(), ...en.custodian },
      minimum: { ...emptyField(), ...en.minimum },
      publishedYield: { ...emptyField(), ...en.publishedYield },
      aum: { ...emptyField(), ...en.aum },
      documents: en.documents || [],
      note: en.note || '',
    });
    setDocsText((en.documents || []).join('\n'));
    setShowForm(true);
  };

  const setField = (key: keyof RegistryFormData, patch: Partial<RegistryFieldData>) => {
    setForm((f) => ({ ...f, [key]: { ...(f[key] as RegistryFieldData), ...patch } }));
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setFormLoading(true);
      const documents = docsText
        .split('\n')
        .map((d) => d.trim())
        .filter(Boolean);
      const payload = { ...form, documents };
      if (editing) {
        await registryActions.updateEntry(editing._id, payload);
        toast({ title: 'Success', description: 'Registry entry updated' });
      } else {
        await registryActions.addEntry(payload);
        toast({ title: 'Success', description: 'Registry entry created' });
      }
      setShowForm(false);
      fetchEntries();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to save entry', variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleteLoading(true);
      await registryActions.deleteEntry(deleteId);
      toast({ title: 'Success', description: 'Entry deleted' });
      setDeleteId(null);
      fetchEntries();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete entry', variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const addTag = async () => {
    if (!newTag.trim()) return;
    try {
      await registryActions.addTag({ label: newTag.trim(), value: newTag.trim().toLowerCase() });
      setNewTag('');
      fetchTags();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to add tag', variant: 'destructive' });
    }
  };

  const removeTag = async (id: string) => {
    try {
      await registryActions.deleteTag(id);
      fetchTags();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete tag', variant: 'destructive' });
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((en) => {
      if (clsFilter !== 'all' && en.assetClass !== clsFilter) return false;
      if (typeFilter === 'real' && !en.real) return false;
      if (typeFilter === 'illustrative' && en.real) return false;
      if (!q) return true;
      return (
        en.name.toLowerCase().includes(q) ||
        en.issuer.toLowerCase().includes(q) ||
        (en.jurisdiction || '').toLowerCase().includes(q)
      );
    });
  }, [entries, search, clsFilter, typeFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Registry</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowTags(true)}>
            <Tag className="h-4 w-4 mr-2" />
            Provenance Tags
          </Button>
          <Button onClick={openAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Add Entry
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <Input
          placeholder="Search name, issuer, jurisdiction"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="md:max-w-sm"
        />
        <Select value={clsFilter} onValueChange={setClsFilter}>
          <SelectTrigger className="md:w-52">
            <SelectValue placeholder="All classes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All classes</SelectItem>
            {REGISTRY_CLASSES.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="md:w-52">
            <SelectValue placeholder="Real + illustrative" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Real + illustrative</SelectItem>
            <SelectItem value="real">Real only</SelectItem>
            <SelectItem value="illustrative">Illustrative only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Registry entries ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Class</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Yield</TableHead>
                <TableHead>AUM</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((en) => (
                <TableRow key={en._id}>
                  <TableCell className="font-medium">
                    {en.name}
                    <div className="text-xs text-muted-foreground">{en.issuer}</div>
                  </TableCell>
                  <TableCell>{en.assetClass}</TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-0.5 text-xs rounded ${
                        en.real ? 'bg-green-200 text-green-800' : 'bg-amber-200 text-amber-800'
                      }`}
                    >
                      {en.real ? 'Real' : 'Illustrative'}
                    </span>
                  </TableCell>
                  <TableCell>{en.publishedYield?.value || '—'}</TableCell>
                  <TableCell>{en.aum?.value || '—'}</TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(en)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setDeleteId(en._id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                    No entries.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Registry Entry' : 'Add Registry Entry'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Product name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Issuer</Label>
                <Input
                  value={form.issuer}
                  onChange={(e) => setForm({ ...form, issuer: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Class</Label>
                <Select
                  value={form.assetClass}
                  onValueChange={(v) => setForm({ ...form, assetClass: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REGISTRY_CLASSES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select
                  value={form.real ? 'real' : 'illustrative'}
                  onValueChange={(v) => setForm({ ...form, real: v === 'real' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="real">Real</SelectItem>
                    <SelectItem value="illustrative">Illustrative</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Oracle</Label>
                <Select
                  value={form.oracleTaxonomy}
                  onValueChange={(v) => setForm({ ...form, oracleTaxonomy: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ORACLE_TAXONOMIES.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Jurisdiction</Label>
              <Input
                value={form.jurisdiction}
                onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
              />
            </div>
            <div>
              <Label>Structure</Label>
              <Textarea
                value={form.structure}
                onChange={(e) => setForm({ ...form, structure: e.target.value })}
              />
            </div>

            {/* Sourced fields with per-field provenance */}
            {(['minimum', 'publishedYield', 'aum', 'custodian'] as const).map((key) => {
              const labels: Record<string, string> = {
                minimum: 'Minimum',
                publishedYield: 'Published Yield',
                aum: 'AUM',
                custodian: 'Custodian',
              };
              const field = form[key];
              return (
                <div key={key} className="rounded border p-3 space-y-2">
                  <p className="text-sm font-medium">{labels[key]}</p>
                  <Input
                    placeholder="Value (leave blank = Not yet sourced)"
                    value={field.value}
                    onChange={(e) => setField(key, { value: e.target.value })}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Select
                      value={field.provenance}
                      onValueChange={(v) => setField(key, { provenance: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Provenance" />
                      </SelectTrigger>
                      <SelectContent>
                        {tags.map((t) => (
                          <SelectItem key={t._id} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="As of (e.g. Q1 2026)"
                      value={field.asOf}
                      onChange={(e) => setField(key, { asOf: e.target.value })}
                    />
                  </div>
                  <Input
                    placeholder="Source URL"
                    value={field.source}
                    onChange={(e) => setField(key, { source: e.target.value })}
                  />
                </div>
              );
            })}

            <div>
              <Label>Documents (one URL per line)</Label>
              <Textarea
                value={docsText}
                onChange={(e) => setDocsText(e.target.value)}
                placeholder={'https://issuer.com/product\nhttps://docs.issuer.com'}
              />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={formLoading}>
                {formLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : editing ? (
                  'Update'
                ) : (
                  'Create'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">Delete this registry entry?</div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} disabled={deleteLoading}>
              No
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleteLoading}>
              {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Yes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tag manager */}
      <Dialog open={showTags} onOpenChange={setShowTags}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Provenance Tags</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2">
            <Input
              placeholder="New tag (e.g. Verified)"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
            />
            <Button onClick={addTag} disabled={!newTag.trim()}>
              Add
            </Button>
          </div>
          <div className="mt-3 space-y-2">
            {tags.map((t) => (
              <div
                key={t._id}
                className="flex items-center justify-between rounded border px-3 py-2"
              >
                <span>
                  {t.label} <span className="text-xs text-muted-foreground">({t.value})</span>
                </span>
                <Button variant="outline" size="sm" onClick={() => removeTag(t._id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {tags.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-4">No tags yet.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegistryManagement;
